import React, { useState, useEffect, useMemo, useRef } from "react";
import { Gauge, AlertTriangle, ShieldCheck, Car, Droplets } from "lucide-react";

/**
 * Wet-braking distance comparison based on independent test data
 * (ADAC, TÜV, Continental, Michelin). Values are approximate averages
 * at the given speed on wet asphalt, with controlled ABS braking.
 *
 *   Tread depth → distance @ 80 km/h
 *     8.0 mm  → ~28 m   (new tire)
 *     1.6 mm  → ~58 m   (legal minimum in EU)
 *
 * For other speeds we scale with v² since kinetic energy scales with v²
 * (braking distance is proportional to v² in the deceleration model).
 */
const REFERENCE_SPEED = 80; // km/h
const REFERENCE_DISTANCE_NEW = 28; // metres
const REFERENCE_DISTANCE_LEGAL = 58; // metres
const SPEEDS = [50, 80, 100, 120];

// Absolute max distance = legal tire @ 120 km/h. Used to scale all bars so
// the visual length grows with speed, communicating the escalating danger.
const MAX_SPEED = 120;
const ABSOLUTE_MAX = Math.round(REFERENCE_DISTANCE_LEGAL * (MAX_SPEED / REFERENCE_SPEED) ** 2);

function distanceAt(speed, baseDist) {
  const factor = (speed / REFERENCE_SPEED) ** 2;
  return Math.round(baseDist * factor);
}

/** Smoothly tweens a number from old value to new value. */
function useCounter(value, duration = 600) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef(null);
  const fromRef = useRef(value);
  const startRef = useRef(null);

  useEffect(() => {
    fromRef.current = display;
    startRef.current = null;
    const step = (ts) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      // ease-out cubic
      const eased = 1 - (1 - t) ** 3;
      const next = Math.round(fromRef.current + (value - fromRef.current) * eased);
      setDisplay(next);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return display;
}

export const SimuladorTravagem = () => {
  const [speed, setSpeed] = useState(80);

  const { newDist, legalDist, diff } = useMemo(() => {
    const n = distanceAt(speed, REFERENCE_DISTANCE_NEW);
    const l = distanceAt(speed, REFERENCE_DISTANCE_LEGAL);
    return { newDist: n, legalDist: l, diff: l - n };
  }, [speed]);

  // Bars are scaled against the absolute maximum so they GROW with speed.
  // Leave 6% of right padding so the car icon doesn't clip the edge.
  const scale = 0.94;
  const newPct = (newDist / ABSOLUTE_MAX) * 100 * scale;
  const legalPct = (legalDist / ABSOLUTE_MAX) * 100 * scale;

  // Animated counters for the distance labels
  const newDistAnim = useCounter(newDist, 700);
  const legalDistAnim = useCounter(legalDist, 800);
  const diffAnim = useCounter(diff, 700);
  const carEquivAnim = useCounter(Math.max(1, Math.round(diff / 4.5)), 700);

  return (
    <section
      data-testid="simulador-travagem"
      id="simulador"
      className="relative pt-28 pb-14 md:pt-32 md:pb-16 bg-[#0E2452] border-t border-[#2F568F] overflow-hidden"
    >
      {/* Decorative rain droplets */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(115deg, transparent 0 24px, #FFFFFF 24px 26px)",
        }}
      />

      <div className="relative w-full px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
            <div>
              <span className="section-label flex items-center gap-2">
                <Droplets className="w-4 h-4" />
                // Simulador de Travagem
              </span>
              <h2 className="font-display font-bold text-white text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight mt-3 leading-[0.98]">
                Piso molhado.
                <br />
                <span className="text-[#FF5A00]">A diferença que salva vidas.</span>
              </h2>
            </div>
            <p className="text-[#A1A1AA] font-body text-sm md:text-base max-w-md">
              Compare a distância de travagem entre um pneu novo (8&nbsp;mm de piso)
              e um pneu no limite legal (1,6&nbsp;mm). Dados baseados em testes
              independentes da ADAC e fabricantes.
            </p>
          </div>

          {/* Speed selector */}
          <div className="mb-7">
            <div className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display mb-3">
              Velocidade do veículo
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`speed-btn-${s}`}
                  onClick={() => setSpeed(s)}
                  className={`flex items-center gap-2 px-5 py-3 font-display font-bold uppercase tracking-wider text-sm border transition-all duration-200 active:scale-95 ${
                    speed === s
                      ? "bg-[#FF5A00] border-[#FF5A00] text-black scale-105 shadow-[0_0_24px_rgba(255,90,0,0.5)]"
                      : "bg-transparent border-[#2F568F] text-white hover:border-[#FF5A00] hover:-translate-y-0.5"
                  }`}
                >
                  <Gauge className="w-4 h-4" />
                  {s} km/h
                </button>
              ))}
            </div>
          </div>

          {/* Comparison */}
          <div className="space-y-8 md:space-y-10">
            {/* New tire — 8mm */}
            <BrakeTrack
              testId="track-new"
              label="Pneu novo"
              treadLabel="8 mm de piso"
              Icon={ShieldCheck}
              accentClass="text-emerald-400"
              barGradient="from-emerald-500/30 to-emerald-500/80"
              skidColor="rgba(16, 185, 129, 0.55)"
              widthPct={newPct}
              distance={newDistAnim}
              distanceColor="text-emerald-400"
            />

            {/* Legal limit — 1.6mm */}
            <BrakeTrack
              testId="track-legal"
              label="Pneu no limite legal"
              treadLabel="1,6 mm de piso"
              Icon={AlertTriangle}
              accentClass="text-[#FF5A00]"
              barGradient="from-[#FF5A00]/30 to-[#FF5A00]/80"
              skidColor="rgba(255, 90, 0, 0.55)"
              widthPct={legalPct}
              distance={legalDistAnim}
              distanceColor="text-[#FF5A00]"
            />
          </div>

          {/* Verdict card */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-2xl bg-[#163A6D] border border-[#2F568F] p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] font-display mb-2">
                Diferença
              </div>
              <div
                data-testid="diff-meters"
                className="font-display font-bold text-white text-3xl md:text-4xl tabular-nums"
              >
                +{diffAnim}&nbsp;m
              </div>
              <div className="text-[#A1A1AA] font-body text-sm mt-2">
                a mais para travar com pneu no limite legal
              </div>
            </div>

            <div className="rounded-2xl bg-[#163A6D] border border-[#2F568F] p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] font-display mb-2">
                Equivale a
              </div>
              <div className="font-display font-bold text-white text-3xl md:text-4xl tabular-nums">
                {carEquivAnim}
              </div>
              <div className="text-[#A1A1AA] font-body text-sm mt-2">
                carros médios de distância adicional
              </div>
            </div>

            <div className="rounded-2xl bg-[#FF5A00] border border-[#FF5A00] p-5 text-black">
              <div className="text-[10px] uppercase tracking-[0.3em] text-black/70 font-display mb-2">
                Recomendação
              </div>
              <div className="font-display font-bold text-2xl md:text-3xl leading-tight">
                Substitua quando o piso atingir 3&nbsp;mm.
              </div>
              <div className="text-black/80 font-body text-sm mt-2">
                Não espere pelo limite legal — a sua segurança não tem limite.
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-[11px] text-[#5A7AAE] font-body mt-6 leading-relaxed">
            * Valores médios estimados em piso molhado com ABS, dados baseados em testes
            independentes da ADAC e fabricantes (Continental, Michelin, Bridgestone). A
            distância real varia consoante o veículo, o estado do piso, a pressão dos
            pneus e o estilo de condução.
          </p>
        </div>
      </div>
    </section>
  );
};

/** Single braking-track row with smooth interpolation when speed changes. */
const BrakeTrack = ({
  testId,
  label,
  treadLabel,
  Icon,
  accentClass,
  barGradient,
  skidColor,
  widthPct,
  distance,
  distanceColor,
}) => (
  <div data-testid={testId}>
    <div className="flex items-baseline justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${accentClass}`} />
        <span className="font-display font-bold uppercase text-white text-base md:text-lg">
          {label}
        </span>
        <span className="text-[#A1A1AA] font-body text-sm">· {treadLabel}</span>
      </div>
      <div
        data-testid={`${testId}-distance`}
        className={`font-display font-bold ${distanceColor} text-2xl md:text-3xl tabular-nums`}
      >
        {distance}&nbsp;m
      </div>
    </div>

    <div className="relative h-14 md:h-16 bg-[#163A6D]/60 border border-[#2F568F] overflow-hidden">
      {/* Skid mark trail — dashed line behind the car */}
      <div
        aria-hidden="true"
        className="absolute bottom-2 left-0 h-1"
        style={{
          width: `${widthPct}%`,
          backgroundImage: `repeating-linear-gradient(90deg, ${skidColor} 0 8px, transparent 8px 14px)`,
          transition: "width 900ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      />

      {/* Bar fill */}
      <div
        className={`absolute inset-y-0 left-0 bg-gradient-to-r ${barGradient}`}
        style={{
          width: `${widthPct}%`,
          transition: "width 900ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      />

      {/* Car icon at the end of the bar */}
      <div
        className="absolute top-1/2 text-white"
        style={{
          left: `${widthPct}%`,
          transform: "translate(-50%, -50%)",
          transition: "left 900ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <Car
          className="w-9 h-9 md:w-10 md:h-10 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)]"
          strokeWidth={1.8}
        />
      </div>

      {/* Start line marker */}
      <div className="absolute inset-y-0 left-0 w-[3px] bg-white/40" aria-hidden="true" />
    </div>
  </div>
);

export default SimuladorTravagem;
